import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import Select from "../../../../../parts/inputs/select";
import { getEffectiveDefaultInsightsLens } from "../../../../../constants/default-insights-lens";
import { getInsightsLensOptions } from "../../../../../utils/insights-lens";
import ProgramInsightsPanelContent from "./ProgramInsightsPanelContent";
import { INSIGHTS_DATA_SOURCE } from "../utils/insightsSetRowMath";

/** @returns {Window|undefined} */
function browserWindow() {
  return globalThis.window;
}

/**
 * Volume / histogram prefs from coach (no panel toggles — General preferences only).
 * @param {Object} [coachPreferences]
 */
function volumePrefsFromCoach(coachPreferences) {
  return {
    intensity:
      coachPreferences?.intensityMeasure === "rir" ? "rir" : "rpe",
    units: coachPreferences?.units === "imperial" ? "imperial" : "metric",
  };
}

/**
 * Collapsible Program Insights region: toolbar, optional expanded metrics from program data,
 * and horizontal scroll kept in sync with the program builder `section.Main` scroll position.
 */
function ProgramInsightsShell({
  cycleId,
  builderScrollRef = undefined,
  coachPreferences,
  builderWeeks,
  programExercises,
  weekHeaderLabels,
  fromMetric,
  insightsDataSource = INSIGHTS_DATA_SOURCE.PRESCRIBED,
  showDataSourceControl = false,
}) {
  const { t, i18n } = useTranslation(["athlete-portal", "dashboard"]);

  /** Whether the lower panel is expanded (always starts collapsed when entering a program). */
  const [expanded, setExpanded] = useState(false);

  /** Panel-local lens only; intensity + units follow coach prefs (see `volumePrefsFromCoach`). */
  const [lens, setLens] = useState(() => getEffectiveDefaultInsightsLens(coachPreferences));

  /** Calendar-only: toggle prescribed targets vs logged outcomes. */
  const [dataSource, setDataSource] = useState(INSIGHTS_DATA_SOURCE.PRESCRIBED);

  const effectiveDataSource = showDataSourceControl
    ? dataSource
    : insightsDataSource;

  /** Wide inner wrapper: width matches builder scrollWidth; horizontal position syncs via panel `scrollLeft`. */
  const mirrorInnerRef = useRef(null);

  /** Expanded panel: `overflow-auto` scrollport (H + V); `scrollLeft` kept in sync with `section.Main`. */
  const panelBodyRef = useRef(null);

  /** Root node — used to tag the stacked layout parent for CSS (more reliable than :has()). */
  const insightsRootRef = useRef(null);

  /**
   * Toggle `data-insights-expanded` on `.ProgramEditor-insights-body--stacked` so layout CSS
   * can shrink the builder strip and grow the panel without depending on :has() support/order.
   */
  useLayoutEffect(() => {
    const root = insightsRootRef.current;
    const stacked = root?.closest(".ProgramEditor-insights-body--stacked");
    if (!(stacked instanceof HTMLElement)) {
      return undefined;
    }
    if (expanded) {
      stacked.dataset.insightsExpanded = "true";
    } else {
      delete stacked.dataset.insightsExpanded;
    }
    return () => {
      delete stacked.dataset.insightsExpanded;
    };
  }, [expanded]);

  /** When the account or coach preferences refresh, realign lens with the server. */
  useEffect(() => {
    setLens(getEffectiveDefaultInsightsLens(coachPreferences));
  }, [coachPreferences?._id, coachPreferences?.defaultInsightsLens]);

  const { intensity, units } = useMemo(
    () => volumePrefsFromCoach(coachPreferences),
    [
      coachPreferences?._id,
      coachPreferences?.intensityMeasure,
      coachPreferences?.units,
    ]
  );

  /** Dropdown options for the LENS control (discipline presets). */
  const lensOptions = useMemo(
    () => getInsightsLensOptions(t),
    [t, i18n.language]
  );

  /** Dropdown options for the DATA SOURCE control (prescribed targets vs logged outcomes). */
  const dataSourceOptions = useMemo(
    () => [
      {
        text: t("programs.programInsights.dataSource.targets"),
        value: INSIGHTS_DATA_SOURCE.PRESCRIBED,
      },
      {
        text: t("programs.programInsights.dataSource.actualResults"),
        value: INSIGHTS_DATA_SOURCE.OUTCOMES,
      },
    ],
    [t, i18n.language]
  );

  /** Navigating to another program/block always starts with insights collapsed. */
  useEffect(() => {
    setExpanded(false);
  }, [cycleId]);

  /** Reset calendar data-source toggle when the visible range changes. */
  useEffect(() => {
    if (showDataSourceControl) {
      setDataSource(INSIGHTS_DATA_SOURCE.PRESCRIBED);
    }
  }, [cycleId, showDataSourceControl]);

  const toggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  /**
   * Match the inner strip width to the builder scroll width and align horizontal scroll.
   * Uses `scrollLeft` on the panel (not `transform`) so native horizontal scrolling works.
   * @param {Element} mainEl
   * @param {Element} innerEl
   * @param {Element|null} panelEl
   */
  const applyMirror = useCallback((mainEl, innerEl, panelEl) => {
    if (!mainEl || !innerEl) {
      return;
    }
    innerEl.style.width = `${mainEl.scrollWidth}px`;
    innerEl.style.removeProperty("transform");
    if (panelEl && panelEl.scrollLeft !== mainEl.scrollLeft) {
      panelEl.scrollLeft = mainEl.scrollLeft;
    }
  }, []);

  /**
   * Subscribe to builder + panel scroll and resize so insights columns stay under week columns.
   */
  useLayoutEffect(() => {
    if (!expanded || !builderScrollRef) {
      return undefined;
    }

    const mainEl = builderScrollRef.current;
    const innerEl = mirrorInnerRef.current;
    /** Capture for cleanup: ref is cleared when the section unmounts on collapse. */
    const panelForEvents = panelBodyRef.current;
    if (!mainEl || !innerEl) {
      return undefined;
    }

    const onMainScroll = () => {
      const panel = panelBodyRef.current;
      if (!panel || panel.scrollLeft === mainEl.scrollLeft) {
        return;
      }
      panel.scrollLeft = mainEl.scrollLeft;
    };

    const onPanelScroll = () => {
      const panel = panelBodyRef.current;
      if (!panel || mainEl.scrollLeft === panel.scrollLeft) {
        return;
      }
      mainEl.scrollLeft = panel.scrollLeft;
    };

    const ro = new ResizeObserver(() => {
      applyMirror(mainEl, innerEl, panelBodyRef.current);
    });

    ro.observe(mainEl);
    const weeksRow = mainEl.querySelector(".weeks");
    if (weeksRow) {
      ro.observe(weeksRow);
    }

    const onWinResize = () => {
      applyMirror(mainEl, innerEl, panelBodyRef.current);
    };

    mainEl.addEventListener("scroll", onMainScroll, { passive: true });
    if (panelForEvents) {
      panelForEvents.addEventListener("scroll", onPanelScroll, { passive: true });
    }
    const win = browserWindow();
    if (win !== undefined) {
      win.addEventListener("resize", onWinResize);
    }
    applyMirror(mainEl, innerEl, panelBodyRef.current);

    return () => {
      ro.disconnect();
      mainEl.removeEventListener("scroll", onMainScroll);
      if (panelForEvents) {
        panelForEvents.removeEventListener("scroll", onPanelScroll);
      }
      if (win !== undefined) {
        win.removeEventListener("resize", onWinResize);
      }
      innerEl.style.removeProperty("width");
      innerEl.style.removeProperty("transform");
    };
  }, [applyMirror, builderScrollRef, expanded]);

  return (
    <div
      ref={insightsRootRef}
      className={`ProgramInsights-root${expanded
          ? " ProgramInsights-root--expanded"
          : " ProgramInsights-root--collapsed"
        }`}
    >
      <div className="ProgramInsights-bar">
        <button
          type="button"
          className="ProgramInsights-bar-hitLayer"
          aria-expanded={expanded}
          aria-controls={
            expanded ? "program-insights-panel-body" : undefined
          }
          aria-label={t("programs.programInsights.toggleButtonAriaLabel")}
          onClick={toggle}
        />
        <div className="ProgramInsights-bar-surface">
          <div className="ProgramInsights-bar-toggle" aria-hidden="true">
            <SvgIcon
              src={
                expanded ? "/img/svg/angle-down.svg" : "/img/svg/angle-right.svg"
              }
              classNames={
                expanded
                  ? "ProgramInsights-bar-toggle-chevron ProgramInsights-bar-toggle-chevron--down"
                  : "ProgramInsights-bar-toggle-chevron ProgramInsights-bar-toggle-chevron--right"
              }
            />
            <SvgIcon
              src="/img/new-icons/stats-chart.svg"
              classNames="ProgramInsights-bar-toggle-icon"
            />
            <span className="ProgramInsights-bar-toggle-title">
              {t("programs.programInsights.toggleTitle")}
              <span className=" text-(--danger) text-xs font-semibold ml-2">
                {t("programs.programInsights.newChip")}
              </span>
            </span>
          </div>
          <span className="ProgramInsights-bar-ellipsis" aria-hidden="true">
            ...
          </span>
          <div className="ProgramInsights-bar-controls">
            <div className="ProgramInsights-control">
              <span className="ProgramInsights-control-label">
                {t("programs.programInsights.lensLabel")}
              </span>
              <div className="ProgramInsights-control-slot">
                <Select
                  name={`program-insights-lens-${String(cycleId)}`}
                  value={lens}
                  onChange={(v) => setLens(v)}
                  options={lensOptions}
                  overlay
                  alignRight
                  openUpward={!expanded}
                />
              </div>
            </div>
            {showDataSourceControl ? (
              <div className="ProgramInsights-control">
                <span className="ProgramInsights-control-label">
                  {t("programs.programInsights.dataSourceLabel")}
                </span>
                <div className="ProgramInsights-control-slot">
                  <Select
                    name={`program-insights-data-source-${String(cycleId)}`}
                    value={effectiveDataSource}
                    onChange={(v) => setDataSource(v)}
                    options={dataSourceOptions}
                    overlay
                    alignRight
                    openUpward={!expanded}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {expanded ? (
        <section
          ref={panelBodyRef}
          id="program-insights-panel-body"
          className="ProgramInsights-panel"
          aria-label={t("programs.programInsights.panelAriaLabel")}
        >
          <div ref={mirrorInnerRef} className="ProgramInsights-panel-wide">
            <ProgramInsightsPanelContent
              lens={lens}
              builderWeeks={builderWeeks}
              programExercises={programExercises}
              weekHeaderLabels={weekHeaderLabels}
              fromMetric={fromMetric}
              insightsDisplayUnits={units}
              insightsIntensityMeasure={intensity}
              insightsDataSource={effectiveDataSource}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
}

ProgramInsightsShell.propTypes = {
  cycleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  builderScrollRef: PropTypes.shape({
    current: PropTypes.any,
  }),
  coachPreferences: PropTypes.object,
  builderWeeks: PropTypes.object,
  programExercises: PropTypes.array,
  weekHeaderLabels: PropTypes.object,
  fromMetric: PropTypes.func,
  insightsDataSource: PropTypes.oneOf([
    INSIGHTS_DATA_SOURCE.PRESCRIBED,
    INSIGHTS_DATA_SOURCE.OUTCOMES,
  ]),
  showDataSourceControl: PropTypes.bool,
};

export default ProgramInsightsShell;
